import { EFirestoreCollection } from '@app/services/shared'
import { arrayRemove, arrayUnion, collection, doc, setDoc, updateDoc } from 'firebase/firestore'

import { db } from '../firebase'
import { getHiddenAccounts, getWallets } from './read'

export const updateUser = async (uid, update) => {
  await updateDoc(doc(db, EFirestoreCollection.ACCOUNTS, `${uid}`), update)
}

export const addUser = async (uid, userData: { email: string }) => {
  await setDoc(doc(db, EFirestoreCollection.ACCOUNTS, uid), {
    email: userData.email,
    registrationStepNo: 0,
    wallets: [],
    errors: [],
    referralCode: doc(collection(db, 'NotARealCollection')).id,
    referralLeadsCount: 0,
    referralConversionsCount: 0,
    referralConversionsPaidCount: 0,
    referralWaitEarned: 0,
  })
}

export const addWallet = async (uid, wallet, nickname) => {
  const wallets = (await getWallets(uid)) || []
  const walletLower = String(wallet || '').toLowerCase()
  const isWalletExist = wallets.find(
    (item) => item.Wallet === wallet || String(item.Wallet || '').toLowerCase() === walletLower,
  )
  if (isWalletExist) return false

  await updateDoc(doc(db, EFirestoreCollection.ACCOUNTS, `${uid}`), {
    wallets: arrayUnion({ Nickname: nickname, Wallet: wallet }),
  })
  return true
}

export const addHiddenAccount = async (uid, account) => {
  const hiddenAccounts = (await getHiddenAccounts(uid)) || []
  const isHiddenAccountExist = hiddenAccounts.find((item) => item.id === account)
  if (isHiddenAccountExist) return false

  await updateDoc(doc(db, EFirestoreCollection.ACCOUNTS, `${uid}`), {
    hiddenAccounts: arrayUnion({ id: account, hidden: true }),
  })
  return true
}

export const addDiscountWallet = async (uid, wallet) => {
  await updateDoc(doc(db, EFirestoreCollection.ACCOUNTS, `${uid}`), {
    discountWallet: wallet,
  })
}

export const removeWallet = async (uid, wallet, nickname) => {
  const wallets = (await getWallets(uid)) || []
  const walletLower = String(wallet || '').toLowerCase()

  let removed = false
  const next = []
  for (const item of wallets) {
    const sameAddress = item.Wallet === wallet || String(item.Wallet || '').toLowerCase() === walletLower
    const sameNickname = item.Nickname === nickname
    if (!removed && sameAddress && sameNickname) {
      removed = true
      continue
    }
    next.push(item)
  }

  if (!removed || next.length !== wallets.length - 1) {
    throw new Error('Wallet entry not found')
  }

  // Rewrite the list (rules allow exactly one removal). arrayRemove no-ops when the
  // stored map doesn't match byte-for-byte and then fails security rules.
  await updateDoc(doc(db, EFirestoreCollection.ACCOUNTS, `${uid}`), {
    wallets: next,
  })
}

/** Remove every entry for an address, one at a time (Firestore rule: size decreases by 1). */
export const removeWalletsByAddress = async (uid, walletAddress: string) => {
  const addr = String(walletAddress || '').toLowerCase()
  let removed = 0
  // Re-read each pass so we always shrink by exactly one
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const wallets = (await getWallets(uid)) || []
    const match = wallets.find((w) => String(w.Wallet || '').toLowerCase() === addr)
    if (!match) break
    await removeWallet(uid, match.Wallet, match.Nickname)
    removed++
    if (removed > 20) break
  }
  return removed
}

export const addTransaction = async (transactionId, transaction) => {
  await setDoc(doc(db, EFirestoreCollection.TRANSACTIONS, transactionId), transaction)
}

export const addErrors = async (uid, err, status) => {
  await updateDoc(doc(db, EFirestoreCollection.ACCOUNTS, `${uid}`), {
    errors: arrayUnion({ Error: err, Status: status }),
  })
}

export const updateErrors = async (uid, err, status) => {
  await updateDoc(doc(db, EFirestoreCollection.ACCOUNTS, `${uid}`), {
    errors: arrayRemove({ Error: err, Status: 'Unresolved' }),
  })
  await updateDoc(doc(db, EFirestoreCollection.ACCOUNTS, `${uid}`), {
    errors: arrayUnion({ Error: err, Status: status }),
  })
}
